import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { MagicWandIcon, PlusIcon } from "@phosphor-icons/react";
import type { ResumeDto } from "@reactive-resume/dto";
import { createResumeSchema } from "@reactive-resume/dto";
import { idSchema } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
} from "@reactive-resume/ui";
import { generateRandomName } from "@reactive-resume/utils";
import slugify from "@sindresorhus/slugify";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useAnalytics } from "@/client/hooks/use-analytics";
import { clearLandingJobId } from "@/client/libs/attribution";
import { openLandingJobAfterStarterResume } from "@/client/pages/onboarding/landing-job-continuity";
import { useCreateResume, useDeleteResume, useUpdateResume } from "@/client/services/resume";
import { useImportResume } from "@/client/services/resume/import";
import { useDialog } from "@/client/stores/dialog";

const formSchema = createResumeSchema.extend({ id: idSchema.optional(), slug: z.string() });

type FormValues = z.infer<typeof formSchema>;

export const ResumeDialog = () => {
  const { isOpen, mode, payload, close } = useDialog<ResumeDto>("resume");
  const { recordJourneyStep } = useAnalytics();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  const { createResume, loading: createLoading } = useCreateResume();
  const { updateResume, loading: updateLoading } = useUpdateResume();
  const { deleteResume, loading: deleteLoading } = useDeleteResume();
  const { importResume: duplicateResume, loading: duplicateLoading } = useImportResume();

  const loading = createLoading || updateLoading || deleteLoading || duplicateLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", slug: "" },
  });

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen, payload]);

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === "title") {
        form.setValue("slug", slugify(values.title ?? ""));
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [form]);

  const onSubmit = async (values: FormValues) => {
    if (isCreate) {
      await createResume({
        slug: values.slug,
        title: values.title,
        visibility: "private",
        ...(payload.prefill ? { data: payload.prefill.data } : {}),
      });
      recordJourneyStep("resume_created", {});
      // A visitor who skipped the import keeps the job they came for: once the
      // starter résumé (and its job profile) exist, open the tailor deep link.
      // Never blocks closing the dialog; with no profile yet it is a no-op.
      if (payload.landingJobId) {
        const landingJobId = payload.landingJobId;
        close();
        await openLandingJobAfterStarterResume(
          queryClient,
          navigate,
          landingJobId,
          clearLandingJobId,
        );
        return;
      }
    }

    if (isUpdate) {
      if (!payload.item?.id) return;

      await updateResume({
        id: payload.item.id,
        title: values.title,
        slug: values.slug,
      });
    }

    if (isDuplicate) {
      if (!payload.item?.id) return;

      await duplicateResume({
        title: values.title,
        slug: values.slug,
        data: payload.item.data,
      });
      recordJourneyStep("resume_created", {});
    }

    if (isDelete) {
      if (!payload.item?.id) return;

      try {
        await deleteResume({ id: payload.item.id });
        recordJourneyStep("resume_deleted", { resume_id: payload.item.id });
      } catch {
        return;
      }
    }

    close();
  };

  const onReset = () => {
    if (isCreate) {
      const title = payload.prefill?.title ?? "";
      form.reset({ title, slug: title ? slugify(title) : "" });
    }
    if (isUpdate)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
    if (isDuplicate) {
      const duplicateTitle = payload.item?.title ?? "";
      form.reset({
        title: t`${duplicateTitle} (Copy)`,
        slug: `${payload.item?.slug}-copy`,
      });
    }
    if (isDelete)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
  };

  const onGenerateRandomName = () => {
    const name = generateRandomName();
    form.setValue("title", name);
    form.setValue("slug", slugify(name));
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete your resume?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone. This will permanently delete your resume and cannot be recovered.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <PlusIcon />
                  <span className="text-lg font-semibold">
                    {isCreate && t`Create a new resume`}
                    {isUpdate && t`Update an existing resume`}
                    {isDuplicate && t`Duplicate an existing resume`}
                  </span>
                </div>
              </DialogTitle>
              <DialogDescription>
                {isCreate && t`Start building your resume by giving it a name.`}
                {isUpdate && t`Changed your mind about the name? Give it a new one.`}
                {isDuplicate && t`Give your old resume a new name.`}
              </DialogDescription>
            </DialogHeader>

            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Title`}</FormLabel>
                  <div className="flex items-center justify-between gap-x-2">
                    <FormControl>
                      <Input {...field} className="flex-1" />
                    </FormControl>

                    {(isCreate || isDuplicate) && (
                      <Tooltip content={t`Generate a random title for your resume`}>
                        <Button
                          size="icon"
                          type="button"
                          variant="outline"
                          aria-label={t`Generate a random name`}
                          onClick={onGenerateRandomName}
                        >
                          <MagicWandIcon />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  <FormDescription>
                    {t`Tip: You can name the resume referring to the position you are applying for.`}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="slug"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Slug`}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={loading}>
                {isCreate && t`Create`}
                {isUpdate && t`Save Changes`}
                {isDuplicate && t`Duplicate`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
